import type { ReactNode } from "react";

/**
 * The portal's few icons, inline: the CSP allows images from its own origin only, and an inline SVG
 * needs no request. They are decorative, so hidden from assistive technology; the text beside each
 * names it.
 */
function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      className="flex-none"
      viewBox="0 0 16 16"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const PAGE_ICONS: Record<string, ReactNode> = {
  quotes: (
    <>
      <path d="M4 1.75h5.5L12.5 4.75v9.5H4z" />
      <path d="M9.5 1.75v3h3" />
      <path d="M6 8.5h4.5" />
      <path d="M6 11h3" />
    </>
  ),
  clients: (
    <>
      <circle cx="8" cy="5.5" r="2.5" />
      <path d="M3 13.5c.5-2.6 2.5-4 5-4s4.5 1.4 5 4" />
    </>
  ),
  logins: (
    <>
      <circle cx="5.25" cy="10.75" r="2.75" />
      <path d="m7.25 8.75 6-6" />
      <path d="m11.25 4.75 1.75 1.75" />
      <path d="m9.5 6.5 1.25 1.25" />
    </>
  ),
  devices: (
    <>
      <rect x="3" y="3" width="10" height="7" rx="1" />
      <path d="M1.75 12.75h12.5" />
    </>
  ),
  metrics: (
    <>
      <path d="M2.5 13.5h11" />
      <path d="M4.5 11V8" />
      <path d="M8 11V4.5" />
      <path d="M11.5 11V6.5" />
    </>
  ),
  accounts: (
    <>
      <circle cx="6" cy="5.5" r="2.25" />
      <path d="M2 13c.4-2.2 2-3.5 4-3.5s3.6 1.3 4 3.5" />
      <path d="M10.5 3.5a2.1 2.1 0 0 1 0 4" />
      <path d="M12 9.8c1 .5 1.7 1.6 2 3.2" />
    </>
  ),
  runs: (
    <>
      <rect x="2.25" y="2.75" width="11.5" height="10.5" rx="2" />
      <path d="M6.5 6v4l3.5-2z" />
    </>
  ),
};

/** The icon of a page in the navigation. */
export function PageIcon({ id }: { id: string }) {
  const icon = PAGE_ICONS[id];
  return icon ? <Icon>{icon}</Icon> : null;
}

/** The portal's mark, beside its name; large on the sign-in card. */
export function Mark({ large }: { large?: boolean }) {
  return (
    <span
      className={`inline-grid flex-none place-items-center bg-mark text-on-mark ${large ? "mb-4 size-10 rounded-lg" : "size-[22px] rounded-md"}`}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 16 16"
        width={large ? 22 : 14}
        height={large ? 22 : 14}
        fill="none"
        focusable="false"
      >
        <path
          d="M8 2.2 13 4.6v3.3c0 2.9-2.1 5.1-5 5.9-2.9-.8-5-3-5-5.9V4.6z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path
          d="m5.8 8 1.5 1.5 3-3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
